import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  ModalState,
  ModalStackItem} from "./types";
import {
  MODAL_CONSTANTS,
} from "./types";

const { BASE_Z_INDEX, Z_INDEX_GAP, MAX_STACK_SIZE } = MODAL_CONSTANTS;

const initialState: ModalState = {
  stack: [],
  modalStates: {},
  nextZIndex: BASE_Z_INDEX,
};

export const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    /**
     * Push a modal onto the stack
     */
    pushModal: (
      state,
      action: PayloadAction<Omit<ModalStackItem, "zIndex">>
    ) => {
      const modal = action.payload;

      // Prevent stack overflow
      if (state.stack.length >= MAX_STACK_SIZE) {
        console.warn(`Maximum modal stack size exceeded (${MAX_STACK_SIZE})`);
        return;
      }

      // Check for duplicate modal
      const existingModal = state.stack.find((m) => m.id === modal.id);
      if (existingModal) {
        console.warn(`Modal with id "${modal.id}" already exists in stack`);
        return;
      }

      // Create modal with z-index
      const zIndex = BASE_Z_INDEX + state.stack.length * Z_INDEX_GAP;
      const stackItem: ModalStackItem = { ...modal, zIndex };

      // Add to stack
      state.stack.push(stackItem);

      // Update next z-index
      state.nextZIndex = zIndex + Z_INDEX_GAP;

      // Initialize modal state
      state.modalStates[stackItem.id] = {
        loading: false,
        error: null,
      };
    },

    /**
     * Pop a modal from the stack by ID
     */
    popModal: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;
      const index = state.stack.findIndex((m) => m.id === modalId);

      if (index === -1) {
        console.warn(`Modal with id "${modalId}" not found in stack`);
        return;
      }

      // Remove from stack
      state.stack.splice(index, 1);

      // Clean up modal state
      delete state.modalStates[modalId];
    },

    /**
     * Pop the topmost modal (LIFO behavior)
     */
    popTopModal: (state) => {
      if (state.stack.length === 0) {
        return;
      }

      const topModal = state.stack[state.stack.length - 1];

      // Remove from stack
      state.stack.pop();

      // Clean up modal state
      delete state.modalStates[topModal.id];
    },

    /**
     * Clear all modals from the stack
     */
    clearModalStack: (state) => {
      state.stack = [];
      state.modalStates = {};
      state.nextZIndex = BASE_Z_INDEX;
    },

    /**
     * Set loading state for a modal
     */
    setModalLoading: (
      state,
      action: PayloadAction<{ modalId: string; loading: boolean }>
    ) => {
      const { modalId, loading } = action.payload;

      if (!state.modalStates[modalId]) {
        state.modalStates[modalId] = { loading: false, error: null };
      }

      state.modalStates[modalId].loading = loading;
    },

    /**
     * Set error state for a modal
     */
    setModalError: (
      state,
      action: PayloadAction<{ modalId: string; error: Error | null }>
    ) => {
      const { modalId, error } = action.payload;

      if (!state.modalStates[modalId]) {
        state.modalStates[modalId] = { loading: false, error: null };
      }

      state.modalStates[modalId].error = error;

      // Clear loading on error
      if (error) {
        state.modalStates[modalId].loading = false;
      }
    },

    /**
     * Clear modal state (loading and error)
     */
    clearModalState: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;
      delete state.modalStates[modalId];
    },

    /**
     * Initialize or ensure modal state exists
     */
    initializeModalState: (state, action: PayloadAction<string>) => {
      const modalId = action.payload;

      if (!state.modalStates[modalId]) {
        state.modalStates[modalId] = { loading: false, error: null };
      }
    },
  },
});

export const {
  pushModal,
  popModal,
  popTopModal,
  clearModalStack,
  setModalLoading,
  setModalError,
  clearModalState,
  initializeModalState,
} = modalSlice.actions;

export default modalSlice.reducer;
